import React, { useState } from 'react';
import { Chip } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { useIssuedCoupons } from '../hooks/useCampaigns';
import { formatDateTime } from '../format';
import type { IssuedCoupon } from '../types';

const COLORS: Record<IssuedCoupon['status'], 'success' | 'default' | 'warning' | 'error'> = {
  UNUSED: 'success',
  USED: 'default',
  EXPIRED: 'warning',
  REVOKED: 'error',
};

/** Coupons the system issued automatically (signup, referral, first payment). */
export const IssuedTab: React.FC<{ campaignId: string }> = ({ campaignId }) => {
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const { data, isLoading } = useIssuedCoupons(
    campaignId,
    pagination.page + 1,
    pagination.pageSize
  );

  const columns: GridColDef<IssuedCoupon>[] = [
    {
      field: 'recipient',
      headerName: 'Recipient',
      flex: 1,
      minWidth: 220,
      valueGetter: (_v, row) => row.recipient?.email ?? '-',
    },
    { field: 'code', headerName: 'Code', width: 210 },
    {
      field: 'issueKey',
      headerName: 'Reason',
      width: 160,
      valueGetter: (_v, row) => row.issueKey ?? '',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => <Chip size="small" label={p.row.status} color={COLORS[p.row.status]} />,
    },
    {
      field: 'notifiedAt',
      headerName: 'Notified',
      width: 120,
      valueGetter: (_v, row) => (row.notifiedAt ? 'Yes' : 'Pending'),
    },
    {
      field: 'issuedAt',
      headerName: 'Issued',
      width: 180,
      valueGetter: (_v, row) => formatDateTime(row.issuedAt),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 180,
      valueGetter: (_v, row) => formatDateTime(row.expiresAt),
    },
  ];

  return (
    <DataGrid
      autoHeight
      rows={data?.items ?? []}
      getRowId={(row) => row.publicId}
      columns={columns}
      loading={isLoading}
      rowCount={data?.total ?? 0}
      paginationMode="server"
      paginationModel={pagination}
      onPaginationModelChange={setPagination}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick
      localeText={{ noRowsLabel: 'No coupons have been issued automatically yet.' }}
    />
  );
};
